import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { Form, Spinner, Alert } from "reactstrap";

import Head from "../layout/head/Head";
import AuthFooter from "./auth/AuthFooter";
import LogoDark2x from "../images/logo-dark2x.png";
import authService from "../auth/authService";
import {
  Block,
  BlockContent,
  BlockDes,
  BlockHead,
  BlockTitle,
  Button,
  Icon,
  PreviewCard,
} from "../components/Component";

interface ResetRequest {
  email: string;
}

const ForgotPassword: React.FC = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [errorVal, setError] = useState<string>("");
  const [submitted, setSubmitted] = useState<boolean>(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ResetRequest>();

  const onFormSubmit = async (formData: ResetRequest) => {
    setLoading(true);
    setError("");
    try {
      await authService.requestPasswordReset({ email: formData.email });
      // Always show the generic confirmation regardless of whether the
      // email is registered — avoids leaking which accounts exist.
      setSubmitted(true);
      setLoading(false);
    } catch (err: any) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Unable to send reset instructions"
      );
      setLoading(false);
    }
  };

  return (
    <>
      <Head title="Forgot Password" />
      <div className="login-container pt-2">
        <div className="brand-logo pb-1 text-center">
          <Link to={process.env.PUBLIC_URL + "/"} className="logo-link">
            <img className="" src={LogoDark2x} alt="logo" />
          </Link>
        </div>
        <div className="d-flex justify-content-center h-100">
          <Block className="nk-block-middle nk-auth-body wide-xs w-50 pb-0">
            <PreviewCard
              className="login-previewCard card-bordered"
              bodyClass="card-inner-lg"
            >
              <BlockHead>
                <BlockContent>
                  <BlockTitle tag="h4">Reset Password</BlockTitle>
                  <BlockDes>
                    <p>
                      Enter the email associated with your account and we&apos;ll
                      send you instructions to reset your password.
                    </p>
                  </BlockDes>
                </BlockContent>
              </BlockHead>

              {errorVal && (
                <div className="mb-3">
                  <Alert color="danger" className="alert-icon">
                    <Icon name="alert-circle" />
                    {errorVal}
                  </Alert>
                </div>
              )}

              {submitted ? (
                <>
                  <div className="mb-3">
                    <Alert color="success" className="alert-icon">
                      <Icon name="check-circle" />
                      If an account exists for that email, reset instructions
                      have been sent.
                    </Alert>
                  </div>
                  <div className="form-note-s2 text-center pt-2">
                    <Link className="link link-primary" to="/login">
                      <Icon name="arrow-left" /> Back to Sign-In
                    </Link>
                  </div>
                </>
              ) : (
                <Form className="is-alter" onSubmit={handleSubmit(onFormSubmit)}>
                  <div className="form-group">
                    <div className="form-label-group">
                      <label className="form-label" htmlFor="reset-email">
                        Email Address
                      </label>
                    </div>
                    <div className="form-control-wrap">
                      <input
                        type="text"
                        id="reset-email"
                        {...register("email", {
                          required: "This field is required",
                        })}
                        placeholder="Enter your email address"
                        className="form-control-lg form-control"
                      />
                      {errors.email && (
                        <span className="invalid">{errors.email.message}</span>
                      )}
                    </div>
                  </div>

                  <div className="form-group">
                    <Button
                      size="lg"
                      className="btn-block"
                      type="submit"
                      color="primary"
                    >
                      {loading ? (
                        <Spinner size="sm" color="light" />
                      ) : (
                        "Send Reset Instructions"
                      )}
                    </Button>
                  </div>

                  <div className="form-note-s2 text-center pt-2">
                    <Link className="link link-primary" to="/login">
                      <Icon name="arrow-left" /> Back to Sign-In
                    </Link>
                  </div>
                </Form>
              )}
            </PreviewCard>
          </Block>
        </div>
        <div className="login-bgi"></div>
      </div>
      <AuthFooter />
    </>
  );
};

export default ForgotPassword;
